import { useState, useEffect, useCallback } from 'react';
import { AUTH_BYPASS, acquireApiToken } from '../config/auth';
import { API_BASE_URL } from '../config/api';

export function useDataRefresh(intervalMinutes = 60) {
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [nextRefresh, setNextRefresh] = useState(
    new Date(Date.now() + intervalMinutes * 60 * 1000)
  );

  const refresh = useCallback(() => {
    setLastRefresh(new Date());
    setNextRefresh(new Date(Date.now() + intervalMinutes * 60 * 1000));
    window.location.reload();
  }, [intervalMinutes]);

  useEffect(() => {
    const timer = setInterval(refresh, intervalMinutes * 60 * 1000);
    return () => clearInterval(timer);
  }, [refresh, intervalMinutes]);

  return { lastRefresh, nextRefresh, refresh };
}

/**
 * Fetch a named data blob from commercial.JsonStore via /api/blob/{key}.
 *
 * #2 (data lineage): the silent static fallback was removed — a missing or
 * failed blob now throws so the page shows a loud error state instead of
 * quietly serving a stale committed /data/*.json snapshot.
 *
 * #6b: store-performance was the last static-file exception (a 12 MB public,
 * no-auth asset). It is now split into authed JsonStore slices
 * (store-performance skeleton + store-perf-wk-* + store-perf-maps), so every
 * dataset flows through the authenticated API and there are no static keys left.
 */
export async function fetchData(filename) {
  const key = filename.replace(/\.json$/i, '');

  // Local dev: skip MSAL entirely when the auth bypass is on (the dev Functions
  // host runs with AUTH_BYPASS too). Mirrors useApi so reporting pages — which use
  // fetchData, not useApi — also work under `VITE_AUTH_BYPASS=true`. No effect in
  // production, where AUTH_BYPASS is always false.
  if (AUTH_BYPASS) {
    const res = await fetch(`${API_BASE_URL}/blob/${key}`);
    if (!res.ok) throw new Error(`Failed to load '${key}' (HTTP ${res.status}).`);
    return (await res.json()).value;
  }

  // Shared token helper: active Majans-tenant account, silent acquisition with
  // an interactive-redirect fallback when silent fails (e.g. the `timed_out`
  // iframe renewal under third-party-cookie blocking).
  const token = await acquireApiToken();
  const res = await fetch(`${API_BASE_URL}/blob/${key}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    // Fail loudly — no stale static fallback. A 404 means the dataset has not
    // been refreshed into JsonStore yet (run the refresh pipeline).
    throw new Error(
      res.status === 404
        ? `Data '${key}' has not been refreshed yet (no blob in JsonStore).`
        : `Failed to load '${key}' (HTTP ${res.status}).`,
    );
  }
  const body = await res.json();
  return body.value;
}

/**
 * Trigger a server-side rebuild of all dashboard data sources.
 * Streams per-script progress events (SSE) to `onEvent`.
 * Resolves when the server sends the final "finished" event.
 * Rejects if the request fails to start or the stream errors out.
 *
 * Event shapes:
 *   {step:"start",    script, label}
 *   {step:"log",      script, label, line}
 *   {step:"done",     script, label, ok:true,  rc, elapsed_sec}
 *   {step:"error",    script, label, ok:false, rc, elapsed_sec}
 *   {step:"finished", total_elapsed_sec, results:[{label,script,ok}]}
 */
export async function refreshFromServer(onEvent) {
  const res = await fetch('/api/refresh', { method: 'POST' });
  if (!res.ok || !res.body) {
    throw new Error(`Refresh request failed: HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let finished = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      for (const line of frame.split('\n')) {
        if (!line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (!payload) continue;
        try {
          const event = JSON.parse(payload);
          onEvent?.(event);
          if (event.step === 'finished') finished = event;
        } catch {
          // Ignore malformed frames.
        }
      }
    }
  }
  return finished;
}
